import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthService } from './auth.service';
import { PocketbaseService } from './pocketbase.service';

@Injectable({
  providedIn: 'root'
})
export class RegisterService {
  constructor(
    private authService: AuthService,
    private pocketbaseService: PocketbaseService,
    private router: Router
  ) { }

  /** Affiliate slug captured from ?ref= on first visit (see AppComponent). */
  private getStoredRef(): string {
    try {
      return (localStorage.getItem('kk_ref') || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  /** utm_source / utm_medium / A/B-Variante aus dem FunnelService (First-Touch, siehe kk_attribution). */
  private getAttributionExtras(): Record<string, string> {
    try {
      const a = JSON.parse(localStorage.getItem('kk_attribution') || 'null');
      const extras: Record<string, string> = {};
      if (a?.source) extras['source'] = a.source;
      if (a?.medium) extras['medium'] = a.medium;
      const variants = localStorage.getItem('kk_variants');
      if (variants) {
        const parsed = JSON.parse(variants);
        const summary = Object.keys(parsed).map(k => `${k}:${parsed[k]}`).join(',').slice(0, 60);
        if (summary) extras['variant'] = summary;
      }
      return extras;
    } catch {
      return {};
    }
  }

  /** Campaign slug captured from ?c= / ?utm_campaign= on first visit (see AppComponent). */
  private getStoredCampaign(): string {
    try {
      return (localStorage.getItem('kk_campaign') || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }



  async register(
    email: string,
    password: string,
    passwordConfirm: string,
    name: string,
    username: string,
    turnstileToken: string,
    profileData: any,
    newsletterOptIn: boolean,
    privacy_policy_hash: string,
    privacy_policy_accepted_date: string,
    pending_redirect?: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      // Registrierungs-Route aufrufen
      const response = await fetch(
        `${environment.pocketbaseUrl}/api/custom/register`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            passwordConfirm,
            name,
            username,
            profileData,
            turnstileToken,
            newsletterOptIn,
            privacy_policy_hash,
            privacy_policy_accepted_date,
            ...(pending_redirect ? { pending_redirect } : {}),
            ...(this.getStoredRef() ? { referred_by: this.getStoredRef() } : {}),
            ...(this.getStoredCampaign() ? { campaign: this.getStoredCampaign() } : {}),
            ...this.getAttributionExtras()
          })
        }
      );

      const result: { success: boolean; message?: string } = await response.json();

      // Debug: log response body
      console.log('[RegisterService] register response body →', result);

      if (!response.ok || !result.success) { // Check both HTTP status and success flag from backend
        throw new Error(result.message || `Server hat einen Registrierungsfehler gemeldet (Status: ${response.status}).`);
      }

      // 2) Automatische Anmeldung
      const loggedIn = await this.authService.login(email, password);
      if (!loggedIn) {
        return {
          success: false,
          message: 'Konto erstellt, aber automatische Anmeldung fehlgeschlagen. Bitte manuell anmelden.'
        };
      }

      // Ensure we have the latest user model fields (e.g. privacy_policy_hash) before the app runs global checks.
      await this.authService.refreshAuth();

      return { success: true };
    }
    catch (err: unknown) {
      let message = 'Registrierung fehlgeschlagen. Bitte erneut versuchen.';
      if (err instanceof Error) {
        message = err.message;
      }
      // Check if it's a network error (fetch specific)
      if (err instanceof TypeError && (err as TypeError).message === 'Failed to fetch') {
        message = 'Verbindung zum Server fehlgeschlagen.';
      }
      return { success: false, message };
    }
  }

  async completeProfile(
    name: string,
    username: string,
    profileData: any,
    acceptedPrivacy: boolean,
    acceptedTerms: boolean,
    newsletterOptIn: boolean,
    privacy_policy_hash: string,
    privacy_policy_accepted_date: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const pb = this.pocketbaseService.getPocketBase();

      const sanitizedProfileData = JSON.parse(JSON.stringify(profileData || {}));
      const profileHash = sanitizedProfileData.privacy_policy_hash;
      const profileAcceptedDate = sanitizedProfileData.privacy_policy_accepted_date;
      delete sanitizedProfileData.privacy_policy_hash;
      delete sanitizedProfileData.privacy_policy_accepted_date;

      const resolvedPrivacyHash = privacy_policy_hash || profileHash || '';
      const resolvedPrivacyAcceptedDate = privacy_policy_accepted_date || profileAcceptedDate || '';

      const payload = {
        name,
        username,
        profileData: sanitizedProfileData,
        acceptedPrivacy,
        acceptedTerms,
        newsletterOptIn,
        privacy_policy_hash: resolvedPrivacyHash,
        privacy_policy_accepted_date: resolvedPrivacyAcceptedDate,
        ...(this.getStoredRef() ? { referred_by: this.getStoredRef() } : {}),
        ...(this.getStoredCampaign() ? { campaign: this.getStoredCampaign() } : {}),
        ...this.getAttributionExtras()
      };

      if (!payload.privacy_policy_hash || !payload.privacy_policy_accepted_date) {
        throw new Error('Privacy policy acceptance payload missing (privacy_policy_hash/privacy_policy_accepted_date).');
      }

      // Force JSON request body to avoid PocketBase SDK auto FormData conversion edge-cases.
      const result = await pb.send('/api/custom/complete-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }) as any;

      console.log('[RegisterService] completeProfile response body →', result);

      await this.authService.refreshAuth();
      return { success: true };
    } catch (err: unknown) {
      let message = 'Profil konnte nicht gespeichert werden. Bitte erneut versuchen.';
      if (err instanceof Error) {
        message = err.message;
      }
      return { success: false, message };
    }
  }
}
